import { endpoints } from 'endpoints.config';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from 'reducers/auth';
import {
    selectVerificationDetailsV3,
    selectVerificationLoading,
    setVerificationStage,
} from 'reducers/verification';
import { Spinner } from 'components/spinner/Spinner';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { Form, Formik } from 'formik';
import { saveContinue, submitForm } from './helpers/helpers';
import api, { ApiResponse, isAxiosErrorHandled } from 'api';
import { validate, validateDocuments, validateOwners } from './validation';
import {
    BusinessVerificationStage,
    VisibleBusinessSteps,
    stageTitles,
} from './BusinessVerificationV3Models';
import { verificationValidationSchema } from './validation';
import { FormValues, initialValue, normalizeOwnerInformationData } from './schema';
import { processedErrors } from 'components/businessVerification/helpers/ExitContinueButtons';
import { RegistrationInformation } from './steps/RegistrationInformation';
import { OperationsInformation } from './steps/OperationsInformation';
import {
    VerificationContext,
    VerificationDropdownOptions,
    ProductServiceItem,
} from './helpers/verificationContext';
import instance from 'api';
import { ExitContinueButtons } from './helpers/ExitContinueButtons';
import { SegmentedProgressBar2 } from 'components/SegmentedProgressBar/SegmentedProgressBar';
import { BeneficialOwners } from './steps/BeneficialOwners';
import { FinalSteps } from './steps/FinalSteps';
import BusinessVerificationDocuments from './steps/BusinessVerificationDocuments';
import classNames from 'classnames';
import { BusinessVerificationComplete } from './steps/BusinessVerificationComplete';
import Button from 'components/button/Button';
import { selectComponentResources } from 'reducers/componentResources';
import { hasOnlyDDAProduct } from './productUtils';

/*
Business onboarding v3 - Copied entire businessVerficationV2 file so v2 can be removed easily at a later date
*/

const PendingScreen = () => {
    return (
        <div className="Waiting">
            <Spinner />
            <h3>Please wait, we are checking your details</h3>
            <p>This may take a few minutes, we'll email you when it's done.</p>
        </div>
    );
};

export interface DocumentRejection {
    proofOfAddress: string[] | null;
    authorizedCapitalDocument: string | null;
    fundsSupportingDocuments: string[] | null;
    bDocumentsUpload: boolean;
}

export const BusinessVerificationV3 = () => {
    const dispatch = useDispatch();
    //rejectedDocuments
    const { stage, rejectedDocuments } = useSelector(selectVerificationDetailsV3); //additionalData to have the stored form data for current stage?
    const loading = useSelector(selectVerificationLoading);
    //persist error state
    const [submitValues, setSubmitValues] = useState<FormValues>();
    const [apiErrors, setApiErrors] = useState<processedErrors>([]);
    const [bManualValidate, setBManualValidate] = useState<boolean>(true);
    const [dropdownData, setDropdownData] = useState<VerificationDropdownOptions | null>(null);
    const [productServicesList, setProductServicesList] = useState<ProductServiceItem[]>([]);
    //fethcing form data state
    const [fetchingFormData, setFetchingFormData] = useState(true);
    const [dataFetched, setDataFetched] = useState(false);
    const [initialValues, setInitialValues] = useState<any>(null);
    const { userInfo } = useSelector(selectComponentResources);
    const companyName = localStorage?.getItem("companyName")?.trim().replace(/\s+/g, "-")

    const hasDDAOnlyProduct = (() => {
        if (!userInfo?.productServices || userInfo.accountType !== 'Business') return false;
        return hasOnlyDDAProduct(userInfo.productServices, productServicesList);
    })();

    useEffect(() => {
        if (stage) {
            if (stage !== BusinessVerificationStage.Complete && !dataFetched) {
                (async () => {
                    try {
                        const response = await api.get(
                            endpoints.businessVerificationV3Module.getFormData
                        );
                        if (response.data.status === '1') {
                            const formData = response.data.details.formData;
                            const customerTrafficCountries =
                                formData.operationsInformation?.customerTrafficCountries;
                            const normalizedCustomerTrafficCountries =
                                typeof customerTrafficCountries === 'string'
                                    ? customerTrafficCountries
                                        .split(',')
                                        .map((s) => s.trim())
                                        .filter(Boolean)
                                    : customerTrafficCountries;
                            const normalizedFormData = {
                                ...formData,
                                ownerInformation: normalizeOwnerInformationData(
                                    formData.ownerInformation
                                ),
                                operationsInformation: {
                                    ...formData.operationsInformation,
                                    customerTrafficCountries: normalizedCustomerTrafficCountries,
                                },
                                businessVerificationStep: formData.businessVerificationStep,
                            };
                            setInitialValues(normalizedFormData);
                            setDataFetched(true);
                        } else if (response.data.status === '0') {
                            Toast.openGenericErrorToast();
                            dispatch(signOut());
                        }
                    } catch (error: any) {
                        if (isAxiosErrorHandled(error) && error.response.status === 404) {
                            setDataFetched(true);
                        } else {
                            Toast.openToastMessage(
                                'Something went wrong checking your verification status, please log in again',
                                ToastMessageReason.ERROR
                            );
                            dispatch(signOut());
                        }
                    }
                    setFetchingFormData(false);
                })();
            } else {
                setFetchingFormData(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage]);

    // Update initialValues when stage changes programmatically after successful submit
    useEffect(() => {
        if (initialValues && stage && dataFetched) {
            // Only update if the stage in initialValues differs from current stage
            if (initialValues.businessVerificationStep !== stage) {
                setInitialValues({
                    ...initialValues,
                    businessVerificationStep: stage,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, dataFetched]);

    useEffect(() => {
        if (!rejectedDocuments) return;
        if (rejectedDocuments?.financialInstitutionForm) {
            dispatch(
                setVerificationStage({
                    currentStage: BusinessVerificationStage.OperationsInformation,
                })
            );
            setBManualValidate(true);
            if (!submitValues && initialValues) {
                setSubmitValues({ ...initialValues });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rejectedDocuments, stage]);

    useEffect(() => {
        if (!initialValues) {
            setInitialValues(initialValue);
        }
        instance
            .get<ApiResponse<VerificationDropdownOptions>>(
                endpoints.businessVerificationV3Module.getDropdownData
            )
            .then((res) => {
                const details: any = res.data.details;

                // Normalise UBO reporting exemptions
                details.uboReportingExemptions = details.uboReportingExemptions
                    ? [{ label: 'Select...', value: null }, ...details.uboReportingExemptions]
                    : undefined;

                // Normalise OpenedDocumentsList -> openedDocumentsList for easier access
                if (!details.openedDocumentsList && details.OpenedDocumentsList) {
                    details.openedDocumentsList = details.OpenedDocumentsList;
                }

                setDropdownData(details);
            });
        instance
            .get<ApiResponse<{ productServices?: ProductServiceItem[] }>>(
                endpoints.registration.businessRegistrationDetailsV2
            )
            .then((res) => {
                if (res.data.status === '1' && res.data.details?.productServices) {
                    setProductServicesList(res.data.details.productServices);
                }
            })
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Scroll modal content to top whenever the current step changes
    useEffect(() => {
        const modalContent = document.querySelector(
            '.Modal.BusinessVerificationModal .ModalContent'
        ) as HTMLElement | null;
        modalContent?.scrollTo({ top: 0, behavior: 'auto' });
    }, [stage]);

    if (loading) {
        return null;
    }

    const chooseStage = () => {
        switch (stage) {
            case BusinessVerificationStage.RegistrationInformation:
                return <RegistrationInformation />;
            case BusinessVerificationStage.OperationsInformation:
                return <OperationsInformation />;
            case BusinessVerificationStage.OwnerInformation:
                return <BeneficialOwners />;
            case BusinessVerificationStage.Terms:
                return <FinalSteps />;
            case BusinessVerificationStage.Documents:
                return <BusinessVerificationDocuments />;
            case BusinessVerificationStage.Complete:
                return <BusinessVerificationComplete />;
        }
    };

    const getValidationProps = () => {
        if (stage === BusinessVerificationStage.Documents) {
            return { validate: validateDocuments };
        }

        if (apiErrors.length > 0 || bManualValidate) {
            return {
                validate: (values: FormValues) =>
                    validate(
                        values,
                        submitValues,
                        apiErrors,
                        verificationValidationSchema[stage as BusinessVerificationStage],
                        rejectedDocuments,
                        stage === BusinessVerificationStage.Terms
                            ? { hasDDAOnly: hasDDAOnlyProduct }
                            : undefined
                    ),
            };
        }

        if (stage === BusinessVerificationStage.OwnerInformation) {
            return { validate: validateOwners };
        }

        return {
            validationSchema:
                verificationValidationSchema[stage as BusinessVerificationStage],
        };
    };


    return (
        <VerificationContext.Provider
            value={{
                selectOptions: dropdownData ?? undefined,
                rejectedDocuments,
                productServices: productServicesList,
            }}
        >
            {loading || fetchingFormData ? (
                <PendingScreen />
            ) : (
                stage && (
                    <Formik
                        initialValues={initialValues}
                        {...getValidationProps()}
                        onSubmit={(values, helper) =>
                            stage === BusinessVerificationStage.Documents
                                ? submitForm(values, helper, dispatch, setApiErrors)
                                : saveContinue(
                                    values,
                                    helper,
                                    dispatch,
                                    stage as BusinessVerificationStage,
                                    setSubmitValues,
                                    setApiErrors
                                )
                        }
                    >
                        {() => (
                            <Form
                                className={classNames(
                                    'BusinessVerificationForm BusinessVerificationFormV3',
                                    {
                                        Wider: stage === BusinessVerificationStage.Documents,
                                    }
                                )}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <a
                                        href={`/${companyName}-Business-Account-Registration-Guide-v.2.pdf`}
                                        download
                                        style={{
                                            fontSize: '12px',
                                            color: '#636fec',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            marginBottom: '20px',
                                        }}
                                    >
                                        Download Registration Guide
                                    </a>
                                    <SegmentedProgressBar2
                                        steps={VisibleBusinessSteps.map((step, i) => ({
                                            stepNumber: i,
                                            label: stageTitles[step],
                                        }))}
                                        currentStepNumber={
                                            stage === BusinessVerificationStage.Complete
                                                ? VisibleBusinessSteps.length - 1
                                                : VisibleBusinessSteps.findIndex(
                                                    (val) => val === stage
                                                )
                                        }
                                    />
                                </div>
                                {chooseStage()}
                                {stage !== BusinessVerificationStage.Complete && (
                                    <ExitContinueButtons />
                                )}
                            </Form>
                        )}
                    </Formik>
                )
            )}
        </VerificationContext.Provider>
    );
};
